import type { CRMUser, SupplierPortalRole, SupplierWorkFlag, User, VendorCapability } from '../types';

export const SUPPLIER_WORK_FLAGS: readonly SupplierWorkFlag[] = ['timber_supplier', 'cnc_supplier'];

export function parseSupplierWorkFlags(raw: unknown): SupplierWorkFlag[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const allowed = SUPPLIER_WORK_FLAGS as readonly string[];
  const seen = new Set<SupplierWorkFlag>();
  for (const x of raw) {
    if (typeof x === 'string' && allowed.includes(x)) {
      seen.add(x as SupplierWorkFlag);
    }
  }
  return SUPPLIER_WORK_FLAGS.filter(flag => seen.has(flag));
}

export function hasSupplierWorkFlag(
  flags: SupplierWorkFlag[] | string[] | undefined,
  flag: SupplierWorkFlag,
): boolean {
  return !!flags?.includes(flag);
}

export function supplierWorkFlagsFromOrgCapabilities(
  capabilities: VendorCapability[] | undefined,
): SupplierWorkFlag[] {
  const caps = capabilities?.length ? capabilities : (['timber'] as VendorCapability[]);
  const out: SupplierWorkFlag[] = [];
  if (caps.includes('timber')) out.push('timber_supplier');
  if (caps.includes('cnc')) out.push('cnc_supplier');
  return out;
}

export function supplierWorkFlagLabel(flag: SupplierWorkFlag): string {
  return flag === 'cnc_supplier' ? 'CNC' : 'Timber';
}

export function userSupplierWorkFlags(
  user: Pick<User, 'supplierDesignations'> | Pick<CRMUser, 'supplierDesignations'> | null | undefined,
): SupplierWorkFlag[] {
  return parseSupplierWorkFlags(user?.supplierDesignations);
}

export const SUPPLIER_PORTAL_ROLES: readonly SupplierPortalRole[] = ['owner', 'employee'];

/** Missing or unknown values default to Owner so existing suppliers keep full financials. */
export function parseSupplierPortalRole(raw: unknown): SupplierPortalRole {
  return raw === 'employee' ? 'employee' : 'owner';
}

export function supplierPortalRoleLabel(role: SupplierPortalRole): string {
  return role === 'employee' ? 'Employee' : 'Owner';
}

/**
 * Staff / non-supplier viewers keep full financials (org preview is not user-scoped).
 * Supplier employees cannot see prices, totals, or quote-submit UI.
 */
export function supplierCanSeeFinancials(
  user:
    | Pick<User, 'role' | 'supplierPortalRole'>
    | Pick<CRMUser, 'role' | 'supplierPortalRole'>
    | null
    | undefined,
): boolean {
  if (!user || user.role !== 'supplier') return true;
  return parseSupplierPortalRole(user.supplierPortalRole) === 'owner';
}
